import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  CloudUpload,
  LibraryMusic,
  VideoLibrary,
  Schedule,
  QueueMusic,
  Devices,
  Speed,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import {
  useAnalyticsStats,
  useSystemHealth,
} from '../hooks/useAnalytics';
import { useRecentContent } from '../hooks/useContent';
import { useAuth } from '../contexts/AuthContext';
import ProcessingStatusCard from '../components/ProcessingStatusCard';
import { BentoGrid, BentoTile, StatTile, QuickActionTile } from '../components/BentoGrid';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGlobalAdmin = user?.global_role === 'global_admin';

  // Hooks for real data
  const { data: analyticsStats, isLoading: statsLoading, error: statsError } = useAnalyticsStats();
  const { data: systemHealth } = useSystemHealth();
  const { data: recentUploads = [], isLoading: uploadsLoading } = useRecentContent(10);

  // Loading state
  if (statsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (statsError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load dashboard data: {(statsError as any)?.message || 'Unknown error'}
      </Alert>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'Processing': return 'warning';
      case 'Queued': return 'default';
      case 'Failed': return 'error';
      default: return 'default';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Quick actions: org users only see Create Playlist (their core daily
  // action). Upload Content and Manage Devices are platform-staff
  // workflows for now — same gating as the sidebar entries.
  const allQuickActions = [
    {
      label: 'Upload Content',
      icon: <CloudUpload />,
      onClick: () => navigate('/upload'),
      color: '#4a9eff',
      requiresGlobalAdmin: true,
    },
    {
      label: 'Create Playlist',
      icon: <QueueMusic />,
      onClick: () => navigate('/scheduling/playlists'),
      color: '#10b981',
    },
    {
      label: 'Manage Devices',
      icon: <Devices />,
      onClick: () => navigate('/fleet/devices'),
      color: '#6366f1',
      requiresGlobalAdmin: true,
    },
  ];
  const quickActions = allQuickActions.filter((a) => !a.requiresGlobalAdmin || isGlobalAdmin);

  return (
    <Box>
      <BentoGrid columns={4} gap={2.5}>
        {/* Welcome Tile - Large */}
        <BentoTile colSpan={2} rowSpan={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box
              component={motion.div}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              sx={{
                width: 72,
                height: 72,
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #4a9eff 0%, #6366f1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {user?.name?.charAt(0).toUpperCase() || 'A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                {getGreeting()}, {user?.name?.split(' ')[0] || 'Admin'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Welcome to your JustPlay dashboard. Here's an overview of your content management system.
              </Typography>
            </Box>
          </Box>
        </BentoTile>

        {/* Stats Tiles */}
        <StatTile
          title="Total Content"
          value={analyticsStats?.total_content?.toLocaleString() || '0'}
          icon={<LibraryMusic />}
          color="#4a9eff"
        />

        {isGlobalAdmin && (
          <StatTile
            title="Processing Queue"
            value={analyticsStats?.processing_queue?.toString() || '0'}
            icon={<Speed />}
            color="#ff6b35"
          />
        )}

        {/* Activity Feed Tile */}
        <BentoTile
          title="Recent Activity"
          subtitle="Latest uploads and changes"
          colSpan={2}
          rowSpan={2}
          noPadding
          accentColor="#4a9eff"
        >
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'rgba(26, 26, 26, 0.95)' }}>Name</TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(26, 26, 26, 0.95)' }}>Type</TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(26, 26, 26, 0.95)' }}>Status</TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(26, 26, 26, 0.95)' }}>Progress</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadsLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : recentUploads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          No recent uploads
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  recentUploads.slice(0, 5).map((upload) => {
                    const isVideo = upload.contentType === 'video';
                    return (
                      <TableRow key={upload.id} hover sx={{ '&:hover': { bgcolor: 'rgba(74, 158, 255, 0.04)' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                              sx={{
                                p: 0.75,
                                borderRadius: '8px',
                                bgcolor: isVideo ? '#ff6b3515' : '#4a9eff15',
                              }}
                            >
                              {isVideo ? (
                                <VideoLibrary sx={{ fontSize: 18, color: '#ff6b35' }} />
                              ) : (
                                <LibraryMusic sx={{ fontSize: 18, color: '#4a9eff' }} />
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {upload.name.length > 25 ? `${upload.name.substring(0, 25)}...` : upload.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isVideo ? 'Video' : 'Audio'}
                            size="small"
                            sx={{
                              bgcolor: isVideo ? '#ff6b3515' : '#4a9eff15',
                              color: isVideo ? '#ff6b35' : '#4a9eff',
                              fontWeight: 500,
                              fontSize: '0.7rem',
                              height: 22,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={upload.status}
                            size="small"
                            color={getStatusColor(upload.status) as any}
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 80 }}>
                            <LinearProgress
                              variant="determinate"
                              value={upload.progress}
                              sx={{
                                flexGrow: 1,
                                height: 4,
                                borderRadius: 2,
                                bgcolor: 'rgba(255, 255, 255, 0.08)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: upload.status === 'Completed' ? '#10b981' :
                                          isVideo ? '#ff6b35' : '#4a9eff',
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </BentoTile>

        {/* More Stats */}
        <StatTile
          title="Active Sessions"
          value={analyticsStats?.active_sessions?.toLocaleString() || '0'}
          icon={<Schedule />}
          color="#10b981"
        />

        <StatTile
          title="Total Plays"
          value={analyticsStats?.total_plays?.toLocaleString() || '0'}
          icon={<TrendingUp />}
          color="#8b5cf6"
        />

        {/* Processing Status - Wide (platform-only) */}
        {isGlobalAdmin && (
          <BentoTile colSpan={2} accentColor="#ff6b35">
            <ProcessingStatusCard embedded />
          </BentoTile>
        )}

        {/* Quick Actions */}
        <QuickActionTile actions={quickActions} />

        {/* System Health (platform-only) */}
        {isGlobalAdmin && (
        <BentoTile title="System Health" accentColor="#10b981">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Storage */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Storage</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {analyticsStats && analyticsStats.storage_limit > 0
                    ? `${Math.round((analyticsStats.storage_used / analyticsStats.storage_limit) * 100)}%`
                    : 'N/A'
                  }
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={analyticsStats && analyticsStats.storage_limit > 0
                  ? (analyticsStats.storage_used / analyticsStats.storage_limit) * 100
                  : 0
                }
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#4a9eff',
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            {/* API Health */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">API Status</Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: systemHealth?.api_status === 'healthy' ? '#10b981' : '#ef4444',
                  }}
                >
                  {systemHealth?.api_status === 'healthy' ? 'Healthy' :
                   systemHealth?.api_status === 'degraded' ? 'Degraded' :
                   systemHealth?.api_status === 'down' ? 'Down' : 'Unknown'}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={systemHealth?.api_status === 'healthy' ? 100 :
                      systemHealth?.api_status === 'degraded' ? 50 : 0}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: systemHealth?.api_status === 'healthy' ? '#10b981' :
                            systemHealth?.api_status === 'degraded' ? '#f59e0b' : '#ef4444',
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            {/* Processing Queue */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Queue</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {analyticsStats?.processing_queue || 0} items
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={analyticsStats?.processing_queue ? Math.min((analyticsStats.processing_queue / 50) * 100, 100) : 0}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#ff6b35',
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
          </Box>
        </BentoTile>
        )}
      </BentoGrid>
    </Box>
  );
};

export default Dashboard;
